/**
 * ChatWidget — floating AI assistant available on all authenticated pages.
 *
 * - ✨ button in the header, next to sign-out
 * - Click opens a chat drawer anchored below the header
 * - iMessage-style bubbles (user right, assistant left)
 * - Maintains conversation history for multi-turn context
 * - Calls POST /api/v1/agent/message { message, history, encounter_id? }
 * - Page-aware: on /encounter/:id/note it picks up the encounter context
 * - Note-edit requests route through the revise pipeline; dispatches
 *   'scribey:note-revised' CustomEvent so NotePage can apply the update
 * - Supports light/dark mode
 */
import { useState, useRef, useEffect, KeyboardEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { X, Send, Sparkles } from 'lucide-react'
import { api } from '../../lib/api'

interface Message {
  role: 'user' | 'assistant'
  content: string
}

export function ChatWidget() {
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const location = useLocation()
  const matchNote = location.pathname.match(/^\/encounter\/([^/]+)\/note/)
  const encounterId = matchNote?.[1]

  // Scroll to latest message when the drawer is open
  useEffect(() => {
    if (open) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, open])

  // Focus input when drawer opens
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  async function handleSend() {
    const text = input.trim()
    if (!text || loading) return

    const userMsg: Message = { role: 'user', content: text }
    const nextMessages = [...messages, userMsg]
    setMessages(nextMessages)
    setInput('')
    setLoading(true)

    try {
      const result = await api.agentMessage(text, messages, encounterId)
      setMessages([...nextMessages, { role: 'assistant', content: result.response }])

      // If the agent revised the note, notify the page
      if (result.note_updated && result.revised_note) {
        window.dispatchEvent(
          new CustomEvent('scribey:note-revised', { detail: result.revised_note })
        )
      }
    } catch (err: any) {
      setMessages([
        ...nextMessages,
        { role: 'assistant', content: `Sorry, something went wrong: ${err.message || 'Please try again.'}` },
      ])
    } finally {
      setLoading(false)
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <>
      {/* Header trigger button (sits next to sign-out) */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Open AI assistant"
        data-tour="assistant"
        title="AI assistant"
        className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors border shadow-sm ${
          open
            ? 'text-brand-600 dark:text-brand-400 bg-brand-50 dark:bg-brand-500/10 border-brand-200 dark:border-brand-500/30'
            : 'text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 hover:text-brand-600 dark:hover:text-brand-400 hover:border-brand-200 dark:hover:border-brand-500/30'
        }`}
      >
        <Sparkles size={16} />
        <span className="hidden md:inline">Assistant</span>
      </button>

      {/* Chat drawer */}
      {open && (
        <div className="fixed top-16 md:top-auto md:bottom-20 right-2 sm:right-4 md:right-6 z-50 w-[340px] sm:w-[380px] max-w-[calc(100vw-1rem)] h-[480px] max-h-[calc(100vh-5rem)] md:max-h-[calc(100vh-8rem)] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden animate-in fade-in slide-in-from-top-4 md:slide-in-from-bottom-4 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-brand-500 dark:bg-brand-600 text-white flex-shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles size={16} />
              <span className="font-semibold text-sm">Dr. Scribey AI</span>
              {encounterId && (
                <span
                  className="bg-white/20 text-white text-[10px] px-1.5 py-0.5 rounded-full"
                  title="I can see the visit you have open and can edit its note"
                >
                  this visit
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {messages.length > 0 && (
                <button
                  onClick={() => setMessages([])}
                  className="text-white/70 hover:text-white text-xs px-2 py-0.5 rounded hover:bg-white/20 transition-colors"
                  title="Clear chat"
                >
                  Clear
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="text-white/80 hover:text-white p-0.5 rounded transition-colors"
                aria-label="Close chat"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Message list */}
          <div className="flex-1 overflow-y-auto px-3 py-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center mt-6 px-4 select-none">
                <Sparkles size={36} className="mx-auto mb-3 text-brand-400 opacity-50" />
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
                  AI Assistant
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                  {encounterId
                    ? "Ask about this visit, have me edit the note ('change the dose to 10mg'), or anything else — letters, codes, clinical questions."
                    : 'Ask me about referrals, letters, clinical questions, billing codes, or anything else you need.'}
                </p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap break-words ${
                    msg.role === 'user'
                      ? 'bg-brand-500 text-white rounded-br-sm'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-sm'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-bl-sm px-4 py-3">
                  <div className="flex gap-1 items-center">
                    <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce [animation-delay:0ms]" />
                    <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce [animation-delay:150ms]" />
                    <span className="w-2 h-2 bg-gray-400 dark:bg-gray-500 rounded-full animate-bounce [animation-delay:300ms]" />
                  </div>
                </div>
              </div>
            )}

            <div ref={bottomRef} />
          </div>

          {/* Input area */}
          <div className="flex-shrink-0 px-3 py-3 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <div className="flex gap-2 items-center">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask anything…"
                disabled={loading}
                className="flex-1 bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:opacity-50 transition-shadow"
              />
              <button
                onClick={handleSend}
                disabled={loading || !input.trim()}
                className="w-9 h-9 flex-shrink-0 rounded-xl bg-brand-600 hover:bg-brand-500 text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                aria-label="Send message"
              >
                <Send size={15} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
